import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Award, AlertCircle, Sparkles, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import './AuditPage.css';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xqewokze';

const QUESTIONS = [
  {
    id: 'company_size',
    label: 'Quelle est la taille de votre entreprise ?',
    options: [
      { value: '1-10', label: '1-10 collaborateurs' },
      { value: '11-50', label: '11-50 collaborateurs' },
      { value: '51-200', label: '51-200 collaborateurs' },
      { value: '201-1000', label: '201-1000 collaborateurs' },
      { value: '1000+', label: 'Plus de 1000 collaborateurs' },
    ],
  },
  {
    id: 'role',
    label: 'Quel est votre rôle dans l\'entreprise ?',
    options: [
      { value: 'DSI', label: 'DSI / CIO' },
      { value: 'DG', label: 'Direction générale' },
      { value: 'manager_it', label: 'Manager IT' },
      { value: 'chef_projet', label: 'Chef de projet' },
      { value: 'autre', label: 'Autre' },
    ],
  },
  {
    id: 'roadmap',
    label: 'Disposez-vous d\'une feuille de route IT formalisée à 12-18 mois ?',
    scored: true,
    options: [
      { value: 'detailed', label: 'Oui, détaillée et suivie', score: 30 },
      { value: 'partial', label: 'Oui, partielle', score: 20 },
      { value: 'verbal', label: 'Informelle / verbale', score: 10 },
      { value: 'none', label: 'Aucune', score: 0 },
    ],
  },
  {
    id: 'methodology',
    label: 'Quel est votre niveau de maturité en gestion de projet ?',
    scored: true,
    options: [
      { value: 'advanced', label: 'Avancé (PMP, Prince2, SAFe, Agile à grande échelle)', score: 30 },
      { value: 'intermediate', label: 'Intermédiaire (Agile/Scrum, méthodologie standardisée)', score: 20 },
      { value: 'basic', label: 'Basique (méthodes ad-hoc, peu structurées)', score: 10 },
      { value: 'none', label: 'Aucune méthodologie formelle', score: 0 },
    ],
  },
  {
    id: 'kpi',
    label: 'Pilotez-vous votre IT avec des KPI / indicateurs ?',
    scored: true,
    options: [
      { value: 'comprehensive', label: 'Oui, dashboard complet avec revue régulière', score: 30 },
      { value: 'partial', label: 'Quelques KPI suivis ponctuellement', score: 20 },
      { value: 'minimal', label: 'Très peu d\'indicateurs', score: 10 },
      { value: 'none', label: 'Aucun KPI formalisé', score: 0 },
    ],
  },
  {
    id: 'challenge',
    label: 'Quel est votre défi IT prioritaire actuel ?',
    options: [
      { value: 'infra', label: 'Modernisation infrastructure / réseaux' },
      { value: 'cloud', label: 'Migration cloud' },
      { value: 'security', label: 'Cybersécurité / conformité' },
      { value: 'transformation', label: 'Transformation digitale' },
      { value: 'governance', label: 'Gouvernance & pilotage projets' },
      { value: 'team', label: 'Renforcement équipe IT' },
    ],
  },
  {
    id: 'budget',
    label: 'Quel est votre budget annuel pour ce projet ?',
    options: [
      { value: '<50k', label: 'Moins de 50 K€' },
      { value: '50-200k', label: '50 - 200 K€' },
      { value: '200k-1M', label: '200 K€ - 1 M€' },
      { value: '>1M', label: 'Plus de 1 M€' },
      { value: 'undecided', label: 'À déterminer' },
    ],
  },
  {
    id: 'timeline',
    label: 'Dans quel délai souhaitez-vous démarrer ?',
    options: [
      { value: 'immediate', label: 'Immédiat (< 1 mois)' },
      { value: '1-3', label: '1 à 3 mois' },
      { value: '3-6', label: '3 à 6 mois' },
      { value: '6+', label: 'Plus de 6 mois' },
      { value: 'exploration', label: 'En phase d\'exploration' },
    ],
  },
];

function getMaturityLevel(score) {
  if (score >= 70) {
    return {
      level: 'Excellence',
      color: '#0052FF',
      icon: Award,
      title: 'Vous êtes en phase d\'excellence opérationnelle',
      description:
        'Votre IT est mature : roadmap claire, méthodologie solide, pilotage par les KPI. MCM peut vous accompagner sur des problématiques avancées de transformation continue, scaling agile, ou coaching de leaders IT.',
      reco: 'Coaching stratégique + audit de transformation continue',
    };
  }
  if (score >= 40) {
    return {
      level: 'Optimisation',
      color: '#F59E0B',
      icon: Sparkles,
      title: 'Vous êtes en phase d\'optimisation',
      description:
        'Vous avez les fondations en place mais des marges d\'amélioration significatives. MCM peut vous aider à structurer davantage, professionnaliser votre pilotage et aligner IT/métier.',
      reco: 'Pilotage de projets + standardisation méthodologique + dashboard KPI',
    };
  }
  return {
    level: 'Structuration',
    color: '#EF4444',
    icon: AlertCircle,
    title: 'Vous êtes en phase de structuration',
    description:
      'Votre IT manque de cadre formalisé. C\'est un excellent moment pour poser les bases : roadmap, méthodologie, gouvernance. MCM intervient sur ce type de mission régulièrement avec des résultats rapides.',
    reco: 'Audit IT + cadrage stratégique + mise en place d\'une gouvernance projet',
  };
}

export default function AuditPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: '', email: '', company: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const totalSteps = QUESTIONS.length + 1; // +1 = contact form
  const progress = Math.round((step / totalSteps) * 100);

  const score = useMemo(() => {
    return QUESTIONS.filter((q) => q.scored).reduce((acc, q) => {
      const ans = answers[q.id];
      if (!ans) return acc;
      const opt = q.options.find((o) => o.value === ans);
      return acc + (opt?.score || 0);
    }, 0);
  }, [answers]);

  const maturity = useMemo(() => getMaturityLevel(score), [score]);

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    setTimeout(() => setStep((s) => s + 1), 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email) {
      toast.error('Merci de renseigner votre nom et email');
      return;
    }
    setSubmitting(true);

    const payload = {
      _subject: `[Audit MCM] ${contact.company || contact.name} – Niveau ${maturity.level} (${score}/90)`,
      name: contact.name,
      email: contact.email,
      company: contact.company,
      phone: contact.phone,
      maturity_level: maturity.level,
      maturity_score: `${score}/90`,
      recommendation: maturity.reco,
      ...QUESTIONS.reduce((acc, q) => {
        const ans = answers[q.id];
        const opt = q.options.find((o) => o.value === ans);
        acc[`Q_${q.label}`] = opt?.label || '—';
        return acc;
      }, {}),
    };

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Audit envoyé !', { description: 'Vous recevrez le récapitulatif par email.' });
      } else {
        toast.error('Erreur d\'envoi', { description: 'Réessayez dans quelques instants.' });
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setSubmitting(false);
    }
  };

  const currentQuestion = QUESTIONS[step];
  const isContactStep = step === QUESTIONS.length;

  // Result screen
  if (submitted) {
    const Icon = maturity.icon;
    return (
      <div className="audit-page">
        <div className="mcm-container audit-container">
          <Link to="/" className="audit-back" data-testid="back-home">
            <ArrowLeft size={18} /> Retour à l'accueil
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="audit-result"
            data-testid="audit-result"
          >
            <div className="result-icon" style={{ background: maturity.color + '15', color: maturity.color }}>
              <Icon size={48} />
            </div>
            <div className="result-label">Niveau de maturité IT</div>
            <h1 className="result-title" style={{ color: maturity.color }}>
              {maturity.level}
            </h1>
            <div className="result-score" data-testid="audit-score">
              {score}<span>/90</span>
            </div>
            <p className="result-description">{maturity.description}</p>
            <div className="result-reco">
              <div className="reco-label">Notre recommandation</div>
              <div className="reco-text">{maturity.reco}</div>
            </div>
            <div className="result-actions">
              <Button
                onClick={() => window.open('https://calendly.com/mohammed-toummini-mentisconsilium/30min', '_blank')}
                className="result-cta-primary"
                data-testid="result-calendly"
              >
                <Calendar size={18} /> Réserver un échange de 30 min
              </Button>
              <Link to="/" className="result-cta-secondary" data-testid="result-home">
                Retour au site
              </Link>
            </div>
            <p className="result-note">
              Un récapitulatif détaillé vient de vous être envoyé par email à {contact.email}.
              Mohammed Toummini vous recontactera sous 48h ouvrées.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-page">
      <div className="mcm-container audit-container">
        <Link to="/" className="audit-back" data-testid="back-home">
          <ArrowLeft size={18} /> Retour à l'accueil
        </Link>

        <div className="audit-header">
          <div className="audit-label">AUDIT IT GRATUIT</div>
          <h1 className="audit-title">Évaluez votre maturité IT en 2 minutes</h1>
          <p className="audit-subtitle">
            8 questions, un score de maturité personnalisé et nos recommandations directement dans votre boîte mail.
            100 % gratuit, sans engagement.
          </p>
        </div>

        <div className="audit-progress-wrapper">
          <div className="audit-progress-bar">
            <motion.div
              className="audit-progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
          <div className="audit-progress-text">
            Étape {Math.min(step + 1, totalSteps)} / {totalSteps} · {progress} %
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!isContactStep && (
            <motion.div
              key={`q-${step}`}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3 }}
              className="audit-question"
              data-testid={`audit-question-${step}`}
            >
              <h2 className="question-text">{currentQuestion.label}</h2>
              <div className="question-options">
                {currentQuestion.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleAnswer(currentQuestion.id, opt.value)}
                    className={`option-button ${answers[currentQuestion.id] === opt.value ? 'selected' : ''}`}
                    data-testid={`option-${currentQuestion.id}-${opt.value}`}
                  >
                    <span>{opt.label}</span>
                    {answers[currentQuestion.id] === opt.value && <CheckCircle2 size={20} />}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <div className="question-nav">
                  <button onClick={() => setStep((s) => s - 1)} className="nav-prev" data-testid="nav-prev">
                    <ArrowLeft size={16} /> Précédent
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {isContactStep && (
            <motion.div
              key="contact-step"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="audit-question audit-contact"
              data-testid="audit-contact-step"
            >
              <h2 className="question-text">Recevez votre score et nos recommandations</h2>
              <p className="contact-intro">
                Votre score actuel : <strong style={{ color: maturity.color }}>{score} / 90</strong> ·{' '}
                niveau <strong>{maturity.level}</strong>
              </p>
              <form onSubmit={handleSubmit} className="contact-form-audit" data-testid="audit-form">
                <div className="form-row">
                  <div className="form-group">
                    <Label htmlFor="audit-name">Nom complet *</Label>
                    <Input
                      id="audit-name"
                      value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })}
                      required
                      maxLength={100}
                      data-testid="audit-name"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="audit-email">Email professionnel *</Label>
                    <Input
                      id="audit-email"
                      type="email"
                      value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                      required
                      maxLength={150}
                      data-testid="audit-email"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <Label htmlFor="audit-company">Entreprise</Label>
                    <Input
                      id="audit-company"
                      value={contact.company}
                      onChange={(e) => setContact({ ...contact, company: e.target.value })}
                      maxLength={150}
                      data-testid="audit-company"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="audit-phone">Téléphone</Label>
                    <Input
                      id="audit-phone"
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                      maxLength={20}
                      data-testid="audit-phone"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="audit-submit"
                  data-testid="audit-submit"
                >
                  {submitting ? 'Envoi…' : (
                    <>
                      Recevoir mon audit <ArrowRight size={18} />
                    </>
                  )}
                </Button>
                <p className="contact-rgpd">
                  En soumettant ce formulaire, vous acceptez d'être recontacté par MCM. Vos données sont traitées
                  conformément à notre <Link to="/politique-confidentialite">politique de confidentialité</Link>.
                </p>
                <button type="button" onClick={() => setStep((s) => s - 1)} className="nav-prev" data-testid="contact-back">
                  <ArrowLeft size={16} /> Modifier mes réponses
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
